import React, { ReactElement, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Translate } from 'react-localize-redux';
import { useDispatch, useSelector } from 'react-redux';
import { formatRelativeTime, translate } from '../../../../shared/helpers/utils';
import {
  AgTableWithRef,
  Button,
  ButtonSizeTypes,
  ButtonType,
  IToastNotification,
  ToasterContainer,
  ToasterMessageType
} from '@digicert/dcone-common-ui';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { getDisplayAppCode } from 'core/constants';
import { AgGridReact } from '@ag-grid-community/react';
import type { GridReadyEvent, ColDef, SelectionChangedEvent } from '@ag-grid-community/core';
import styles from './GroupAccess.module.scss';
import GroupAccessDrawer from './GroupAccessDrawer';
import { fetchRolesByAccountId, assignRolesToGroup } from '../../Groups.actions';
import { fetchTenantById } from 'secure-pages/tenants/Tenants.actions';
import { IAppState } from '../../../../core/store/store';
import { GroupAccessData, GroupAccessProps, RoleRow } from 'secure-pages/groups/Groups.types';

const agGridLicenseKey = process.env.REACT_APP_AG_GRID_LICENSE_KEY || '';

export const GroupAccess = ({ groupId, groupData, loading: propsLoading, onRefresh }: GroupAccessProps): ReactElement => {
  const dispatch = useDispatch();
  const mainTableRef = useRef<AgGridReact>(null);
  const [loading, setLoading] = useState(true);
  const [gridApi, setGridApi] = useState<any>();
  const [showDrawer, setShowDrawer] = useState(false);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [groupName, setGroupName] = useState(groupData?.name || 'Group');
  const [accessData, setAccessData] = useState<GroupAccessData[]>([]);
  const [context, setContext] = useState<any>();

  const [selectedRoles, setSelectedRoles] = useState<Record<string, string[]>>({});

  const [availableServices, setAvailableServices] = useState<{ value: string; label: string; disabled?: boolean }[]>([]);
  const [accountApplications, setAccountApplications] = useState<any[]>([]);
  const [serviceRoleData, setServiceRoleData] = useState<Record<string, { account: string; sp_account_id: string; sp_account_friendly_name: string; roles: RoleRow[] }>>({});
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<IToastNotification[]>([]);
  const profileApplications = useSelector((state: IAppState) => state.profile?.data?.applications || {});

  const FILTER_STORAGE_KEY = `group-access-table-filters-${groupId}`;

  useEffect(() => {
    if (gridApi) {
      gridApi.setFilterModel(null);
      localStorage.removeItem(FILTER_STORAGE_KEY);
    }
  }, [groupId]);

  const hideNotification = useCallback((id: string | number) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const handleFilterChanged = useCallback(() => {
    if (gridApi) {
      const filterModel = gridApi.getFilterModel();
      try {
        localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filterModel));
      } catch (error) {
        console.error('Error saving filters to localStorage:', error);
      }
    }
  }, [gridApi, FILTER_STORAGE_KEY]);

  const humanizeServiceKey = useCallback((serviceKey: string): string => {
    if (!serviceKey) return '';
    return serviceKey
      .split(/[^a-zA-Z0-9]+/)
      .filter(Boolean)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }, []);

  const getServiceName = useCallback((appIdOrCode: string): string => {
    if (!appIdOrCode) return 'Unknown Service';

    const appsArray = Array.isArray(profileApplications)
      ? profileApplications
      : Object.values(profileApplications);

    const app = appsArray.find((a: any) =>
      a.id === appIdOrCode || a.app_code === appIdOrCode
    );

    if (app) {
      return (app as any).name || (app as any).app_name || humanizeServiceKey((app as any).app_code || appIdOrCode);
    }
        return humanizeServiceKey(appIdOrCode);
  }, [profileApplications, humanizeServiceKey]);

  useEffect(() => {
    const accountId = groupData?.account_id;
    if (!accountId) {
      setAccountApplications([]);
      return;
    }

    const fetchAccountApplications = async () => {
      try {
        const account: any = await dispatch(fetchTenantById(accountId));
        const applications = Array.isArray(account?.applications)
          ? account.applications
          : Object.values(account?.applications || {});
        setAccountApplications(applications as any[]);
      } catch (err: any) {
        console.error('Failed to fetch account applications:', err);
        setAccountApplications([]);
      }
    };

    fetchAccountApplications();
  }, [dispatch, groupData?.account_id]);

  useEffect(() => {
    const appsArray = Array.isArray(accountApplications)
      ? accountApplications
      : Object.values(accountApplications || {});

    if (!appsArray || appsArray.length === 0) {
      console.warn('No applications found in profile');
      setAvailableServices([]);
      return;
    }

    const services = appsArray.map(app => {
      const appCode = app.app_code || getDisplayAppCode(app.short_app_code) || app.id;
      const hasRoles = serviceRoleData[appCode] && serviceRoleData[appCode].roles.length > 0;

      return {
        value: appCode,
        label: app.name || app.app_name || humanizeServiceKey(appCode),
        disabled: !hasRoles
      };
    });

    setAvailableServices(services);
  }, [accountApplications, serviceRoleData, humanizeServiceKey]);

  useEffect(() => {
    const accountId = groupData?.account_id;
    if (!accountId) return;

    const fetchRoles = async () => {
      setRolesLoading(true);
      setRolesError(null);
      try {
        const data = await dispatch(fetchRolesByAccountId(accountId));
        if (!data || typeof data !== 'object') {
          console.warn('Invalid roles API response:', data);
          setRolesError('Invalid response from roles API');
          return;
        }

        const roleMap: Record<string, { account: string; sp_account_id: string; sp_account_friendly_name: string; roles: RoleRow[] }> = {};

        Object.entries(data).forEach(([serviceKey, payloadArray]) => {
          if (!Array.isArray(payloadArray) || payloadArray.length === 0) {
            console.warn(`No data found for service: ${serviceKey}`);
            return;
          }

          const payload = payloadArray[0];
          const accountsList = payload?.account_roles || [];

          if (!Array.isArray(accountsList) || accountsList.length === 0) {
            console.warn(`No accounts found for service: ${serviceKey}`);
            return;
          }

          const application = profileApplications && typeof profileApplications === 'object'
            ? Object.values(profileApplications).find((app: any) => app.app_code === serviceKey)
            : null;
          const applicationId = application?.id || '';

          const allRoles: RoleRow[] = [];
          let serviceAccountName = '';
          let serviceAccountId = '';
          let serviceAccountFriendlyName = '';

          accountsList.forEach((account: any) => {
            const spAccountId = account?.sp_account_id;
            const spAccountName = account?.sp_account_name;
            const spAccountFriendlyName = account?.sp_account_friendly_name;

            if (!serviceAccountName && spAccountName) {
              serviceAccountName = spAccountName;
            }
            if (!serviceAccountId && spAccountId) {
              serviceAccountId = spAccountId;
            }
            if (!serviceAccountFriendlyName && spAccountFriendlyName) {
              serviceAccountFriendlyName = spAccountFriendlyName;
            }

            const accountRoles = account?.roles || [];
            accountRoles.forEach((role: any) => {
              if (role?.id) {
                allRoles.push({
                  id: role.id,
                  roleName: role.display_name || role.name || 'Unnamed Role',
                  applicationId: applicationId,
                  serviceProviderAccountId: spAccountId,
                  source_account_friendly_identifier: account?.source_account_friendly_identifier || 'Unknown Account'
                });
              }
            });
          });

          if (allRoles.length > 0) {
            roleMap[serviceKey] = {
              account: serviceAccountName || 'Unknown Account',
              sp_account_id: serviceAccountId || '',
              sp_account_friendly_name: serviceAccountFriendlyName || '',
              roles: allRoles
            };
          }
        });

        setServiceRoleData(roleMap);
      } catch (err: any) {
        const errorMsg = err?.message || 'Unable to load roles';
        setRolesError(errorMsg);
        console.error('Failed to fetch roles:', err);
      } finally {
        setRolesLoading(false);
      }
    };

    fetchRoles();
  }, [groupData?.account_id, humanizeServiceKey, selectedServices.length]);

  useEffect(() => {
    if (groupData) {
      setGroupName(groupData.name);
      const mappedData: GroupAccessData[] = (groupData.roles || []).map((role: any) => {
        const app = (groupData.applications || []).find((a: any) => a.id === role.application_id);
        const serviceName = app ? (app.app_name || app.name) : getServiceName(role.application_id || role.app_code);

        return {
          id: role.id,
          service: serviceName,
          account: role.source_account_name || 'N/A',
          accountId: role.source_account_friendly_identifier,
          role: role.role_name || role.role_id,
          assignedBy: role.created_by || 'System',
          assignedOn: role.created_at || ''
        };
      });

      setAccessData(mappedData);
      setLoading(false);
      if (gridApi) {
        try {
          const savedFilters = localStorage.getItem(FILTER_STORAGE_KEY);
          if (savedFilters) {
            const filterModel = JSON.parse(savedFilters);
            gridApi.setFilterModel(filterModel);
          }
        } catch (error) {
          console.error('Error reapplying filters after data update:', error);
        }
      }
    } else if (!propsLoading) {
      setLoading(false);
    }
  }, [groupData, propsLoading, gridApi, FILTER_STORAGE_KEY, getServiceName]);

  const onFilterOpened = useCallback((params: any) => {
    if (params.column.getColDef().filter !== 'agTextColumnFilter') return;
    requestAnimationFrame(() => {
      const inputs = params.eGui?.querySelectorAll('input.ag-input-field-input');
      inputs?.forEach((input: HTMLInputElement) => {
        input.value = '';
      });
    });
  }, []);

  const onGridReady = useCallback((params: GridReadyEvent<any>) => {
    setGridApi(params.api);
    try {
      const savedFilters = localStorage.getItem(FILTER_STORAGE_KEY);
      if (savedFilters) {
        const filterModel = JSON.parse(savedFilters);
        params.api.setFilterModel(filterModel);
      }
    } catch (error) {
      console.error('Error restoring filters from localStorage:', error);
    }

    const gridContext = {
      additionalFilters: {},
      updateGlobalSearchFilters: (filters: any) => {
        if (filters.additionalFilters) {
          const newContext = {
            ...gridContext,
            additionalFilters: filters.additionalFilters
          };
          params.api.setGridOption('context', newContext);
          params.api.onFilterChanged();
        }
      }
    };

    setContext(gridContext);
    params.api.setGridOption('context', gridContext);
  }, [FILTER_STORAGE_KEY]);

  const onModelUpdated = useCallback((params: any) => {
    if (params.api) {
      const rowCount = params.api.getDisplayedRowCount();
      if (rowCount === 0) {
        params.api.showNoRowsOverlay();
      } else {
        params.api.hideOverlay();
      }
    }
  }, []);

  const columns: ColDef[] = useMemo(() => {
    const uniqueServices = Array.from(new Set(accessData.map(item => item.service))).sort();
    const uniqueAccounts = Array.from(new Set(accessData.map(item => item.account))).sort();

    return [
      {
        colId: 'service',
        field: 'service',
        headerName: translate('accounts.details.scim.groups.service') as string,
        flex: 1,
        minWidth: 150,
        sortable: true,
        filter: uniqueServices.length > 0 ? 'agSetColumnFilter' : false,
        lockVisible: true,
        filterParams: {
          filterOptions: ['equals'],
          defaultOption: 'equals',
          values: uniqueServices,
          suppressSelectAll: true,
          suppressMiniFilter: false,
          defaultToNothingSelected: true,
          textFormatter: (value: string) => {
            if (!value || value.trim() === '' || value === 'undefined') {
              return null;
            }
            return value.trim();
          }
        },
        cellRenderer: (params: any) => <span>{params.value}</span>
      },
      {
        colId: 'account',
        field: 'account',
        headerName: translate('accounts.details.scim.groups.account') as string,
        flex: 1.5,
        minWidth: 180,
        autoHeight: true,
        wrapText: true,
        sortable: true,
        filter: uniqueAccounts.length > 0 ? 'agSetColumnFilter' : false,
        filterParams: {
          filterOptions: ['equals'],
          defaultOption: 'equals',
          values: uniqueAccounts,
          suppressSelectAll: true,
          suppressMiniFilter: false,
          defaultToNothingSelected: true,
          textFormatter: (value: string) => {
            if (!value || value.trim() === '' || value === 'undefined') {
              return null;
            }
            return value.trim();
          }
        },
        cellRenderer: (params: any) => (
          <div className={styles.accountCell}>
            <div>{params.value}</div>
            {params.data.accountId && (
              <div className={styles.accountId}>
                {params.data.accountId}
              </div>
            )}
          </div>
        )
      },
      {
        colId: 'role',
        field: 'role',
        headerName: translate('accounts.details.scim.groups.role') as string,
        flex: 1.5,
        minWidth: 200,
        sortable: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          suppressAndOrCondition: true,
          maxNumConditions: 1,
          textFormatter: (value: string) => value.trim(),
          textMatcher: ({ filterOption, value, filterText }: any) => {
            if (!filterText) return true;
            const filterTextLowerCase = filterText.toLowerCase();
            const valueLowerCase = value ? value.toString().toLowerCase() : '';
            return valueLowerCase.includes(filterTextLowerCase);
          },
          closeOnApply: true,
          buttons: ['apply']
        },
        cellRenderer: (params: any) => {
          return <span>{params.value}</span>;
        }
      },
      {
        colId: 'assignedBy',
        field: 'assignedBy',
        headerName: translate('accounts.details.scim.groups.assignedBy') as string,
        flex: 1,
        minWidth: 120,
        sortable: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains'],
          defaultOption: 'contains',
          suppressAndOrCondition: true,
          maxNumConditions: 1,
          textMatcher: ({ filterOption, value, filterText }: any) => {
            if (!filterText) return true;
            const filterTextLowerCase = filterText.trim().toLowerCase();
            const valueLowerCase = value ? value.toString().trim().toLowerCase() : '';
            return valueLowerCase.includes(filterTextLowerCase);
          },
          closeOnApply: true,
          buttons: ['apply']
        }
      },
      {
        colId: 'assignedOn',
        field: 'assignedOn',
        headerName: translate('accounts.details.scim.groups.assignedOn') as string,
        flex: 1.5,
        minWidth: 180,
        sortable: true,
        filter: false,
        valueFormatter: (params: any) => formatRelativeTime(params.value) || '',
        comparator: (valueA: string, valueB: string) => {
          const dateA = new Date(valueA).getTime();
          const dateB = new Date(valueB).getTime();
          return dateA - dateB;
        }
      }
    ];
  }, [translate, accessData]);

  const handleUpdateAccess = () => {
    if (groupData && groupData.applications && groupData.roles) {
      const appCodes = groupData.applications.map((app: any) => app.app_code);
      setSelectedServices(appCodes);
      const rolesByAppCode: Record<string, string[]> = {};

      groupData.roles.forEach((role: any) => {
        const app = groupData.applications.find((a: any) => a.id === role.application_id);
        if (app && app.app_code) {
          if (!rolesByAppCode[app.app_code]) {
            rolesByAppCode[app.app_code] = [];
          }
          rolesByAppCode[app.app_code].push(role.role_id);
        }
      });

      setSelectedRoles(rolesByAppCode);
    }

    setShowDrawer(true);
  };

  const handleAssignAccess = async () => {
    try {
      let payload;

      if (assignedRolesList.length === 0) {
        payload = {
          remove_all: true,
          group_roles: []
        };
      } else {
        const rolesByService: Record<string, any[]> = {};

        assignedRolesList.forEach(role => {
          if (!rolesByService[role.serviceKey]) {
            rolesByService[role.serviceKey] = [];
          }

          const roleData: any = {
            role_id: role.roleId
          };

          if (role.serviceProviderAccountId) {
            roleData.sp_account_id = role.serviceProviderAccountId;
          }

          rolesByService[role.serviceKey].push(roleData);
        });
        payload = {
          remove_all: false,
          group_roles: Object.entries(rolesByService).map(([appCode, roles]) => ({
            app_code: appCode,
            roles: roles
          }))
        };
      }

      console.log('Assign roles payload:', JSON.stringify(payload, null, 2));

      await dispatch(assignRolesToGroup(groupId, payload));

      if (onRefresh) {
        await onRefresh();
      }

      setNotifications([
        ...notifications,
        {
          type: ToasterMessageType.SUCCESS,
          description: `Access assigned to ${groupName}`,
          id: `access-assigned-${Date.now()}`
        }
      ]);

      setShowDrawer(false);
      setSelectedServices([]);
      setSelectedRoles({});
    } catch (err) {
      console.error('Failed to assign roles:', err);
    }
  };

  const handleCancel = () => {
    setSelectedServices([]);
    setSelectedRoles({});
    setShowDrawer(false);
  };

  const removeAssignedRole = (serviceKey: string, roleId: string) => {
    setSelectedRoles(prev => ({
      ...prev,
      [serviceKey]: (prev[serviceKey] || []).filter(id => id !== roleId)
    }));
  };
  const assignedRolesList = Object.entries(selectedRoles).flatMap(([serviceKey, roleIds]) => {
    const service = availableServices.find(s => s.value === serviceKey);
    const serviceData = serviceRoleData[serviceKey];
    if (!service || !serviceData || !roleIds.length) return [];
    return roleIds.map(roleId => {
      const role = serviceData.roles.find(r => r.id === roleId);
      return role
        ? {
          serviceLabel: service.label,
          account: serviceData.account,
          roleName: role.roleName,
          serviceKey,
          roleId,
          applicationId: (role as any).applicationId,
          serviceProviderAccountId: (role as any).serviceProviderAccountId
        }
        : null;
    }).filter(Boolean) as Array<{ serviceLabel: string, account: string, roleName: string, serviceKey: string, roleId: string, applicationId: string, serviceProviderAccountId?: string }>;
  });

  return (
    <section className={styles.groupAccess}>
      <header className={styles.header}>
        <h1 className={styles.heading}><Translate id='accounts.details.scim.groups.groupAccess' /></h1>
        <div className={styles.actions}>
          <Button buttonType={ButtonType.PRIMARY} size={ButtonSizeTypes.SMALL} onClick={handleUpdateAccess}>
            <Translate id='accounts.details.scim.groups.updateGroupAccess' />
          </Button>
        </div>
      </header>

      <div className={styles.tableWrapper}>
        <AgTableWithRef
          ref={mainTableRef}
          columnDefs={columns}
          rowData={accessData}
          rowModelType="clientSide"
          modules={[ClientSideRowModelModule]}
          licenseKey={agGridLicenseKey}
          onGridReady={onGridReady}
          onFilterOpened={onFilterOpened}
          onModelUpdated={onModelUpdated}
          onFilterChanged={handleFilterChanged}
          shouldShowTopActionBar={true}
          domLayout='normal'
          className={styles.table}
          suppressCellFocus={true}
          disableSSRMRowSelection={true}
          pagination={true}
          paginationPageSize={10}
          paginationPageSizeSelector={[10, 25, 50, 100]}
          context={context}
        />
      </div>

      <GroupAccessDrawer
        open={showDrawer}
        onClose={handleCancel}
        groupName={groupName}
        availableServices={availableServices}
        selectedServices={selectedServices}
        setSelectedServices={setSelectedServices}
        serviceRoleData={serviceRoleData}
        selectedRoles={selectedRoles}
        setSelectedRoles={setSelectedRoles}
        assignedRolesList={assignedRolesList}
        removeAssignedRole={removeAssignedRole}
        handleAssignAccess={handleAssignAccess}
        handleCancel={handleCancel}
        hasExistingRoles={accessData.length > 0}
      />
      <ToasterContainer hideNotification={hideNotification} notifications={notifications} />
    </section>
  );
};

export default GroupAccess;
